
import React, { useState, useMemo, useEffect } from 'react';
import ApiEndpointTable from './ApiEndpointTable';
import ApiEndpointFormModal from './ApiEndpointForm';
import ConfirmationModal from '../../../../components/shared/ConfirmationModal';
import BulkActionsBar, { BulkAction } from '../../../../components/shared/BulkActionsBar';
import { TrashIcon } from '../../../../components/icons';
import { useWebhooksPage } from '../hooks/useWebhooksPage';
import Pagination from '../../../../components/shared/Pagination';

interface IncomingEndpointsTabProps {
    page: ReturnType<typeof useWebhooksPage>;
}

const IncomingEndpointsTab: React.FC<IncomingEndpointsTabProps> = ({ page }) => {
    const bulkActions: BulkAction[] = [
        { label: 'Excluir', icon: TrashIcon, onClick: () => page.setIsEndpointBulkDeleteOpen(true), variant: 'danger' }
    ];

    // Paginação
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    const paginatedEndpoints = useMemo(() => {
        const firstPageIndex = (currentPage - 1) * pageSize;
        const lastPageIndex = firstPageIndex + pageSize;
        return page.filteredEndpoints.slice(firstPageIndex, lastPageIndex);
    }, [currentPage, page.filteredEndpoints, pageSize]);

    useEffect(() => {
        setCurrentPage(1);
    }, [page.searchTerm]);

    return (
        <>
            <ApiEndpointTable
                endpoints={paginatedEndpoints}
                onEdit={page.openEditEndpoint}
                onDelete={page.openDeleteEndpoint}
                onRegenerateToken={page.openRegenerateToken}
                selectedIds={page.endpointSelectedIds}
                onToggleSelection={page.toggleEndpointSelection}
                onToggleAll={page.toggleAllEndpoints}
                getEndpointUrl={page.getEndpointUrl}
            />

            <Pagination
                currentPage={currentPage}
                totalCount={page.filteredEndpoints.length}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
            />

            <ApiEndpointFormModal
                isOpen={page.isEndpointFormOpen}
                onClose={() => page.setIsEndpointFormOpen(false)}
                onSave={page.saveEndpoint}
                endpointToEdit={page.endpointToEdit}
                generateToken={page.generateToken}
                generateSlug={page.generateSlug}
                getEndpointUrl={page.getEndpointUrl}
            />

            <ConfirmationModal
                isOpen={page.isEndpointDeleteOpen}
                onClose={() => page.setIsEndpointDeleteOpen(false)}
                onConfirm={page.confirmDeleteEndpoint}
                title="Excluir Endpoint"
                message={`Excluir "${page.endpointToDelete?.name}"?`}
                confirmButtonText="Excluir"
                confirmButtonColor="red"
            />

            <ConfirmationModal
                isOpen={page.isEndpointBulkDeleteOpen}
                onClose={() => page.setIsEndpointBulkDeleteOpen(false)}
                onConfirm={page.bulkDeleteEndpoints}
                title="Excluir Endpoints"
                message={`Excluir ${page.endpointSelectedIds.size} endpoint(s)?`}
                confirmButtonText="Excluir"
                confirmButtonColor="red"
            />

            <ConfirmationModal
                isOpen={page.isRegenerateTokenOpen}
                onClose={() => page.setIsRegenerateTokenOpen(false)}
                onConfirm={page.confirmRegenerateToken}
                title="Regenerar Token"
                message={`Regenerar token de "${page.endpointToRegenerate?.name}"? O token atual será invalidado.`}
                confirmButtonText="Regenerar"
                confirmButtonColor="red"
            />

            <BulkActionsBar
                selectedCount={page.endpointSelectedIds.size}
                actions={bulkActions}
                onClose={page.clearEndpointSelection}
            />
        </>
    );
};

export default IncomingEndpointsTab;
